import type { DocumentNode, OperationVariables, TypedDocumentNode } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useRef } from 'react';

export function useSafeMutation<TData, TVariables extends OperationVariables = OperationVariables>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
) {
  const [mutate, state] = useMutation<TData, TVariables>(document);
  const mutateRef = useRef(mutate);
  const inFlightRef = useRef<ReturnType<typeof mutate> | null>(null);

  mutateRef.current = mutate;

  function safeMutate(options?: Parameters<typeof mutate>[0]) {
    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    type MutationOptions = NonNullable<Parameters<typeof mutate>[0]>;

    const nextOptions = options
      ? {
        ...options,
        context: {
          ...(options.context ?? {}),
          idempotencyKey: crypto.randomUUID(),
        },
      }
      : {
        context: {
          idempotencyKey: crypto.randomUUID(),
        },
      };

    const executeMutation = mutateRef.current as (options: MutationOptions) => ReturnType<typeof mutate>;
    const promise = executeMutation(nextOptions as MutationOptions).finally(() => {
      inFlightRef.current = null;
    });

    inFlightRef.current = promise;

    return promise;
  }

  return [safeMutate, state] as const;
}